/**
 * Match-level rules: the conditions that end a game (RULES.md §8).
 *
 * Movegen answers "what may I play"; this answers "is the game over". Threefold
 * repetition lives here rather than in movegen because it is a property of the game's
 * *history*, not of a position — which is also why it has no effect on perft.
 */

import { generateLegalMoves, isCheckmate, legalMoves, MoveList } from './movegen.ts';
import {
  clonePosition,
  type FastPosition,
  makeMove,
  MOVE_PASS,
  MOVE_RESIGN,
  moveType,
} from './position.ts';
import { packFromMove } from './movegen.ts';
import type { MatchStatus, Move, PlayerIndex, Variant } from './types.ts';
import { initialPosition } from './position.ts';

/** Occurrences of the same position needed for a draw (§8.1). */
export const REPETITION_LIMIT = 3;

/**
 * The identity of a position for repetition purposes (§8.1): piece placement, the
 * player to move, and **both players' remaining spawn counts**.
 *
 * Spawns are part of it because they are an exhaustible resource — the same arrangement
 * of pieces with 9 spawns in hand is not the same position as one with 2, and omitting
 * them would let a player claim a draw from a materially different position.
 *
 * An exact string rather than a hash: this runs once per move, never in a search loop,
 * and a hash collision here would silently draw a live game. The bot's transposition
 * table will want a Zobrist hash, but that is a different job with different stakes.
 */
export function positionKey(pos: FastPosition): string {
  // `cells` is an Int8Array of small values; String.fromCharCode over it is compact and
  // avoids building an array of digits.
  let s = '';
  for (let i = 0; i < pos.cells.length; i++) s += String.fromCharCode(pos.cells[i]);
  return `${s}|${pos.playerToMove}|${pos.spawns[0]}|${pos.spawns[1]}`;
}

export interface GameOutcome {
  readonly status: MatchStatus;
  readonly winner?: PlayerIndex;
}

export const PLAYING: GameOutcome = { status: 'playing' };

/**
 * A game in progress: a position, the moves played, and enough history to detect
 * repetition. Used by the server to run a match, by the tutorial to run a scripted one,
 * and by tests.
 *
 * Clock, players and chat are deliberately absent — those belong to the match record,
 * not to the rules.
 */
export class Game {
  readonly position: FastPosition;
  readonly log: Move[] = [];

  /** How many times each position has occurred, including the current one. */
  private readonly seen = new Map<string, number>();
  private outcome: GameOutcome = PLAYING;

  constructor(public readonly variant: Variant, start?: FastPosition) {
    this.position = start ? clonePosition(start) : initialPosition(variant);
    this.seen.set(positionKey(this.position), 1);
    this.refreshOutcome();
  }

  get status(): MatchStatus {
    return this.outcome.status;
  }

  get winner(): PlayerIndex | undefined {
    return this.outcome.winner;
  }

  get isOver(): boolean {
    return this.outcome.status !== 'playing';
  }

  legalMoves(): Move[] {
    return this.isOver ? [] : legalMoves(this.position);
  }

  isLegal(move: Move): boolean {
    if (this.isOver) return false;
    if (move.type === 'resign') return true; // always available, never generated (§8)

    const target = packFromMove(move);
    const list = new MoveList();
    generateLegalMoves(this.position, list);
    for (let i = 0; i < list.length; i++) {
      if (list.at(i) === target) return true;
    }
    return false;
  }

  /**
   * Play a move, updating the outcome. Throws if the move is not legal here, so the
   * server can rely on this rather than validating separately.
   */
  play(move: Move): GameOutcome {
    if (this.isOver) {
      throw new Error(`Cannot move in a game that ended in ${this.outcome.status}`);
    }
    if (!this.isLegal(move)) {
      throw new Error(`Illegal move: ${JSON.stringify(move)}`);
    }

    const mover = this.position.playerToMove;

    if (move.type === 'resign') {
      this.log.push(move);
      this.outcome = { status: 'resignation', winner: (mover ^ 1) as PlayerIndex };
      return this.outcome;
    }

    makeMove(this.position, packFromMove(move));
    this.log.push(move);

    const key = positionKey(this.position);
    this.seen.set(key, (this.seen.get(key) ?? 0) + 1);

    this.refreshOutcome();
    return this.outcome;
  }

  /** How many times the current position has occurred. */
  repetitionCount(): number {
    return this.seen.get(positionKey(this.position)) ?? 0;
  }

  private refreshOutcome(): void {
    // Checkmate first: it ends the game immediately (§6), and a checkmated player has
    // no moves, so nothing else can be true at the same time.
    if (isCheckmate(this.position)) {
      this.outcome = {
        status: 'checkmate',
        winner: (this.position.playerToMove ^ 1) as PlayerIndex,
      };
      return;
    }

    if (this.repetitionCount() >= REPETITION_LIMIT) {
      this.outcome = { status: 'repetition' };
      return;
    }

    // Two passes in succession is a draw offered and accepted (§8).
    const n = this.log.length;
    if (n >= 2 && this.log[n - 1].type === 'pass' && this.log[n - 2].type === 'pass') {
      this.outcome = { status: 'drawn' };
      return;
    }

    this.outcome = PLAYING;
  }
}

/** Whether a packed move ends the turn without changing the board. */
export const isQuietMove = (packed: number): boolean => {
  const t = moveType(packed);
  return t === MOVE_PASS || t === MOVE_RESIGN;
};
